// Returns the value of the first group in the array where predicate is true, and undefined otherwise.
// utility functions for cart
import {
    CART_GROUP_TYPE_PRODUCT,
    CART_GROUP_TYPE_SERVICE,
    CartGroup,
    CartGroupNameErrorMessage,
    CartGroupRaw,
    CartItem,
    CartSampling,
    deserializeCartGroupRaw
} from '@app/model/cart.model';
import { trimCartItems } from '@app/order-checkout/shared/cart';
import { State } from '@app/store/cart/cart.reducers';
import { LimbusErrorResponse, LimbusErrorStatus, ResponseMessage } from '@app/model/limbus-error-response.model';
import { OrderStatus } from '@app/constants/order-status';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpStatus } from '@app/constants/HttpStatusCodes';

export const getCartGroupType = (resourceType = '') => {
    const tokens = resourceType.split('.');
    return tokens[0];
};
export const unavailableCartItemsPresent = (items): boolean => items.some((item) => (item.length > 0));
export const sumGroupItems = (groups) => groups ? groups.reduce(
    (sum, group) => {
        if (group && group.items) {
            return sum + group.items.length;
        }
        return sum;
    }, 0) : 0;
export const sumGroupUnavailableItems = (groups: (CartGroup | CartGroupRaw)[]) => groups.reduce(
    (sum, group) => {
        if (group && group.unavailableItems) {
            return sum + group.unavailableItems.length;
        }
        return sum;
    }, 0);
export const setSamplingGroupErrorMessage = (groups: CartGroup[], groupId: number, errorMessage: CartGroupNameErrorMessage) =>
    groups.map((group: CartGroup) => {
        if (group.id === groupId) {

            const updatedGroup = group.clone();
            updatedGroup.groupNameError = errorMessage;

            return updatedGroup;
        }
        return group;
    });
export const updatedGroupWithUnavailableItems = (cartGroupsFromRequest: CartGroupRaw[]) =>
    cartGroupsFromRequest.map((group: CartGroupRaw) => deserializeCartGroupRaw(group));
export const setUnavailableCartItems = (groups: CartGroupRaw[]) => groups.map((group: CartGroupRaw) => [...group.unavailableItems]);
export const getCartGroupById = (groups: CartGroup[], groupId: number) => groups.find(group => group.id === groupId);
export const getServiceGroupItems = (groups: CartGroup[]) => {
    const serviceGroups = groups.filter(
        group => getCartGroupType(group.resourceType) === CART_GROUP_TYPE_SERVICE);
    return serviceGroups.reduce((prev, next) => [...prev, ...next.items],
        []);
};
export const getSamplingSupplyGroupItems = (groups: CartGroup[]): CartItem[] => {
    const serviceGroups = groups.filter(
        group => getCartGroupType(group.resourceType) === CART_GROUP_TYPE_PRODUCT);
    return serviceGroups.reduce((prev, next) => [...prev, ...next.items],
        []);
};
export const addRecommendedSupplies = (items: CartItem[], state: State) => trimCartItems(
    [...items, ...state.recommendSupplies]
);
export const getAddingItemsToCartWithout = (id: number, ids: { number?: number }) => Object.keys(ids).reduce((object, key) => {
    if (key !== `${id}`) {
        object[key] = ids[key];
    }
    return object;
}, {});

// case reducers
export const addSamplingSupplyGroupId = (groups: CartGroup[], payloadGroup: CartGroupRaw) => {
    let exists = false;
    const newGroups = groups.map((group: CartGroup) => {
        if (group.id === payloadGroup.id) {
            exists = true;
        }
        return group;
    });

    if (!exists) {
        return [...groups, CartGroup];
    }
    return newGroups;
};

export const setSamplingSuppliesSampling = (groups: CartGroupRaw[]) => {
    const newGroups = groups.filter(group => getCartGroupType(group.resourceType) === CART_GROUP_TYPE_PRODUCT);
    return newGroups.length > 0
        ? new CartSampling(newGroups[0].id, newGroups[0].name)
        : new CartSampling(null, 'Sampling Supplies');
};

export interface LimbusErrorWithStatus {
    error: LimbusErrorResponse;
    status: OrderStatus;
}

export const voucherErrorReference = 'voucherReference';

const isVoucherError = (response: HttpErrorResponse) => {
    const hasReference = response.status === 400 &&
  !!response.error &&
  !!response.error.messages &&
  !!response.error.messages[0] &&
  !!response.error.messages[0].reference;

    if (hasReference) {
        const { reference } = response.error.messages[0];
        return reference === voucherErrorReference;
    }

    return false;

};

export const buildLimbusErrorResponseWithStatus = (response: HttpErrorResponse): LimbusErrorWithStatus => {

    let errorResponse: LimbusErrorResponse = response.error;
    let orderStatus: OrderStatus;

    if (isVoucherError(response)) {
        errorResponse = new LimbusErrorResponse(
            new LimbusErrorStatus(
                400,
                'Bad Request'),
            [
                new ResponseMessage(
                    voucherErrorReference,
                    response.error.messages[0].message
                )
            ],
            ['Invalid promo code']
        );
        orderStatus = OrderStatus.BAD_REQUEST;

    } else if (!!errorResponse && (errorResponse.status === null || errorResponse.status === undefined)) {
        errorResponse = new LimbusErrorResponse(
            new LimbusErrorStatus(
                500,
                'Internal Server Error'),
            [new ResponseMessage('reference', 'client side network issue')],
            ['Error in connecting to server']
        );
        orderStatus = OrderStatus.FAIL_INTERNAL_SERVER;
    } else if (response.status === 404) {
        errorResponse = new LimbusErrorResponse(
            new LimbusErrorStatus(404, 'Cart Unavailable'),
            [new ResponseMessage(
                'reference',
                'There was an error in fetching cart from server. Please refresh the page and try again')],
            ['Error in fetching cart from server']
        );
        orderStatus = OrderStatus.FAIL_INTERNAL_SERVER;
    } else {
        orderStatus = (errorResponse.status.value === HttpStatus.INTERNAL_SERVER_ERROR)
            ? OrderStatus.FAIL_INTERNAL_SERVER
            : OrderStatus.FAIL;
    }

    return { error: errorResponse, status: orderStatus };

};

export const setCartUpdateError = (cartUpdateError, groupId: number) => {
    if (!!cartUpdateError && !!groupId && groupId === cartUpdateError.groupId) {
        return null;
    }
    return cartUpdateError;
};
